import { Link } from 'react-router-dom';
import { MapPin, Radar, ShieldCheck } from 'lucide-react';
import { buttonClass } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useFormato } from '@/hooks/useFormato';
import { useI18n } from '@/hooks/useI18n';
import { MosaicoMexico } from '@/components/mapa/MosaicoMexico';
import { useSesion } from './identidad/useSesion';
import { BarraDeSesion } from './identidad/BarraDeSesion';
import { useMensajeDeAcceso } from './identidad/useMensajeDeAcceso';
import { useCifras } from './datos/useCifras';
import type { Cobertura, Embudo, PuntoDeSerie } from './datos/tipos';
import { RUTA_DEMO, RUTA_PORTERO } from './rutas';
import styles from './area.module.css';

/**
 * El panel: cobertura territorial y embudo comercial.
 *
 * Recuperado de `073f62f^`, recableado dos veces y **ya en su sitio**: llamaba a
 * `obtenerMetricas()` y `verCoberturaDeInversionista()` —dos servicios que se
 * fueron con el backend el 2026-09-18—, pasó por `CIFRAS_PUBLICAS`, un archivo
 * declarado, y desde el 2026-09-21 pide `GET /panel/resumen` a `capital` **con
 * el token**.
 *
 * Ese último salto es el que cierra el contenido, y no es cosmético: mientras
 * las cifras viajaban en el bundle las leía quien insistiera, con sesión o sin
 * ella. El login protegía la **aplicación**, no los **bytes**. Por eso aquel
 * archivo no podía tener ni un número, y por eso ahora sí los hay.
 *
 * Los cuatro estados de una vista con datos, que antes se contestaban en el
 * tipo, ahora se contestan de verdad:
 *
 * | Estado | Dónde |
 * |---|---|
 * | Carga | `useCifras`, y puede durar ~74 s: la Aurora se pausa sola |
 * | Vacío | las dos secciones lo pintan: `cobertura === null`, `hayDatos` |
 * | Error | con el código del puerto, y con reintentar |
 * | Sin conexión | es el código `red`, que ya distingue el adaptador |
 *
 * **Quién decide qué se ve es el servidor.** La respuesta trae el agregado ya
 * construido, sin un solo nombre de organización dentro: lo garantiza
 * `para_inversionista()` en `capital`, que es una función distinta de la de
 * administración, y una prueba suya que serializa la respuesta y busca cada
 * nombre del directorio.
 */
export function PanelInversionistaPage() {
  const { t } = useI18n();
  const inv = t.area.inversionistas;
  const formato = useFormato();
  const { usuario } = useSesion();

  useDocumentMeta(t.meta.panelInversionista);

  const { cifras, cargando, error, reintentar } = useCifras();
  const mensaje = useMensajeDeAcceso();

  // Carga y error se pintan ANTES de la cabecera y no dentro: la cabecera dice
  // «actualizado al <corte>», y sin cifras no hay corte que decir. Enseñarla
  // con una fecha vacía o inventada es peor que no enseñarla.
  if (cargando || error || !cifras) {
    return (
      <div className={styles.page}>
        <div className="ilu-container">
          <header className={styles.head}>
            <h1 className={styles.title}>{inv.titulo}</h1>
          </header>
          {error ? (
            <div className={styles.card}>
              <p className={styles.vacio} role="alert">
                {mensaje(error)}
              </p>
              <button type="button" className={buttonClass({ variant: 'primary' })} onClick={reintentar}>
                {t.area.comun.reintentar}
              </button>
            </div>
          ) : (
            /*
              `role="status"` y no un spinner mudo: quien usa lector de pantalla
              tiene que enterarse de que hay algo en curso. Y el texto avisa de
              que puede tardar, porque la Aurora se pausa sola y la primera
              petición del día la paga --74 s medidos en dev--.
            */
            <p className={styles.vacio} role="status">
              {inv.cargando}
            </p>
          )}
        </div>
      </div>
    );
  }

  const { corte, cobertura, embudo } = cifras;

  return (
    <div className={styles.page}>
      <div className="ilu-container">
        <header className={styles.head}>
          <h1 className={styles.title}>{inv.titulo}</h1>
          <p className={styles.subtitle}>{inv.actualizado(formato.fechaDeCalendario(corte))}</p>
          <BarraDeSesion />
        </header>

        {/*
          El aviso no es cortesía: quien mira estas cifras debe saber que no
          está viendo una muestra parcial, sino un agregado deliberado. Si no,
          la primera pregunta será "¿y puedo ver quiénes son?".
        */}
        <p className={[styles.aviso, styles.avisoNeutro].join(' ')} role="status">
          <ShieldCheck size={16} aria-hidden="true" style={{ verticalAlign: '-3px' }} />{' '}
          {inv.avisoAgregadoAntes}
          <strong>{inv.avisoAgregadoFuerte}</strong>
          {inv.avisoAgregadoDespues}
        </p>

        {/*
          **La demo va ANTES de las cifras**, desde el 2026-09-21.

          El orden era cobertura → demo → embudo, y decía algo que no queríamos
          decir: primero enséñame los números, y si te quedan ganas, mira el
          producto. Para quien evalúa invertir la primera pregunta no es cuánta
          tracción hay, es **si esto existe**; y hoy, con el embudo en su estado
          vacío y la cobertura recién cargada, las cifras son lo más flojo que
          tiene esta pantalla. Lo más fuerte es que el producto se puede abrir y
          usar.

          El panel enseña tracción comercial; la demo enseña el producto. Siguen
          siendo dos preguntas distintas del mismo visitante y siguen separadas:
          lo que cambia es cuál se contesta primero.
        */}
        <div className={styles.card} style={{ marginBottom: 24 }}>
          <h2 className={styles.cardTitle}>
            <Radar size={16} aria-hidden="true" /> {inv.demo.titulo}
          </h2>
          <p className={styles.metricHint} style={{ marginBottom: 16 }}>
            {inv.demo.descripcion}
          </p>
          <Link to={RUTA_DEMO} className={buttonClass({ variant: 'primary' })}>
            {inv.demo.cta}
          </Link>
        </div>

        <SeccionCobertura cobertura={cobertura} />

        {/*
          Sólo para administradores, y **sólo para no pintar un enlace que da
          403**. La ruta existe igual y se alcanza escribiéndola: esconderla no
          es lo que la cierra, lo que la cierra es el servidor.
        */}
        {usuario?.rol === 'administrador' && (
          <p style={{ marginBottom: 24 }}>
            <Link to={RUTA_PORTERO}>{t.area.portero.titulo}</Link>
          </p>
        )}

        <h2 className={styles.cardTitle} style={{ marginTop: 32 }}>
          {inv.embudo.titulo}
        </h2>
        <SeccionEmbudo embudo={embudo} />
      </div>
    </div>
  );
}

/**
 * Cobertura territorial: dónde ya hay contactos en campo.
 *
 * Va antes del embudo porque contesta la primera pregunta de quien evalúa
 * —«¿esto existe fuera de una presentación?»— con un mapa que no se puede
 * fabricar sin haber ido.
 */
function SeccionCobertura({ cobertura }: { cobertura: Cobertura | null }) {
  const { t } = useI18n();
  const c = t.area.inversionistas.cobertura;
  const formato = useFormato();

  return (
    <div className={styles.card} style={{ marginBottom: 24 }}>
      <h2 className={styles.cardTitle}>
        <MapPin size={16} aria-hidden="true" /> {c.titulo}
      </h2>
      <p className={styles.metricHint} style={{ marginBottom: 18 }}>
        {c.subtitulo}
      </p>

      {/*
        El estado vacío, que es el que está en vigor. No se esconde la sección:
        un mapa ausente se lee como que no hay cobertura; un mapa apagado que
        dice por qué se lee como lo que es.
      */}
      {!cobertura ? (
        <p className={styles.vacio}>{c.todaviaNo}</p>
      ) : (
        <>
          <div className={styles.cards} style={{ marginBottom: 20 }}>
            <Tarjeta etiqueta={c.kpis.registros} valor={formato.numero(cobertura.kpis.registros)} />
            <Tarjeta
              etiqueta={c.kpis.organizaciones}
              valor={formato.numero(cobertura.kpis.organizaciones)}
            />
            <Tarjeta
              etiqueta={c.kpis.estados}
              valor={`${cobertura.kpis.estadosConSenal}/${cobertura.kpis.totalDeEstados}`}
            />
            <Tarjeta
              etiqueta={c.kpis.internacionales}
              valor={formato.numero(cobertura.kpis.conexionesInternacionales)}
            />
            {/* Trabajo de campo en curso, no un hueco: hay tarjetas
                fotografiadas esperando transcripción. */}
            <Tarjeta
              etiqueta={c.kpis.pendientes}
              valor={formato.numero(cobertura.kpis.pendientes)}
            />
          </div>

          <MosaicoMexico porEstado={cobertura.porEstado} />

          <div style={{ marginTop: 24 }}>
            <Distribucion titulo={c.porTipo} datos={cobertura.porTipo} />
          </div>

          <p className={styles.metricHint} style={{ marginTop: 16 }}>
            {c.nota}
          </p>
        </>
      )}
    </div>
  );
}

function SeccionEmbudo({ embudo }: { embudo: Embudo }) {
  const { t } = useI18n();
  const inv = t.area.inversionistas;
  const formato = useFormato();
  const { resumen } = embudo;

  /*
    Seis ceros o una frase, nunca las dos cosas.

    Mientras el formulario web y el alta de cuentas no registren nada, las
    tarjetas dirían `0` en las seis, y **un cero afirma que se midió**. La
    bandera no se deduce aquí de los seis números: la trae el dato, porque es el
    servidor quien sabe si las tablas están vacías o si es que no hubo nada.
  */
  if (!resumen.hayDatos) {
    return (
      <p className={[styles.aviso, styles.avisoNeutro].join(' ')} role="status">
        {inv.embudo.sinDatos}
      </p>
    );
  }

  return (
    <>
      <div className={styles.cards}>
        <Tarjeta
          etiqueta={inv.tarjetas.solicitudesDeContacto}
          valor={formato.numero(resumen.solicitudesDeContacto)}
        />
        <Tarjeta
          etiqueta={inv.tarjetas.cuentasCreadas}
          valor={formato.numero(resumen.cuentasCreadas)}
        />
        <Tarjeta
          etiqueta={inv.tarjetas.pidieronDemo}
          valor={formato.numero(resumen.pidieronDemo)}
          destacado
        />
        <Tarjeta etiqueta={inv.tarjetas.enEspera} valor={formato.numero(resumen.enEspera)} />
        <Tarjeta
          etiqueta={inv.tarjetas.demosAtendidas}
          valor={formato.numero(resumen.demosAtendidas)}
          exito
        />
        <Tarjeta
          etiqueta={inv.tarjetas.conversionADemo}
          valor={`${resumen.conversionADemoPorcentaje}%`}
          pista={inv.tarjetas.conversionPista}
          destacado
        />
      </div>

      <div className={styles.grid2}>
        <Distribucion titulo={inv.distribuciones.porIndustria} datos={embudo.porIndustria} />
        <Distribucion titulo={inv.distribuciones.porTamano} datos={embudo.porTamanoEmpresa} />
        <Distribucion
          titulo={inv.distribuciones.porTipoDeSolicitud}
          datos={embudo.porTipoDeSolicitud}
        />
        <Distribucion titulo={inv.distribuciones.porPerfil} datos={embudo.porPerfil} />
      </div>

      <h2 className={styles.cardTitle} style={{ marginTop: 32 }}>
        {inv.evolucionMensual}
      </h2>
      <div className={styles.grid2}>
        <Serie titulo={inv.series.cuentasPorMes} datos={embudo.altasPorMes} />
        <Serie titulo={inv.series.solicitudesPorMes} datos={embudo.solicitudesPorMes} />
      </div>
    </>
  );
}

/**
 * Una cifra.
 *
 * Recibe el valor **ya formateado**, al revés que la versión anterior, que
 * recibía un número o una cadena y decidía dentro. El motivo es la prueba que
 * recorre el panel buscando números sin declarar: si el componente formatea, el
 * separador de millares lo pone él y la prueba tiene que deshacerlo para
 * comparar. Formateando fuera, lo que se declara y lo que se pinta son la misma
 * cadena.
 */
function Tarjeta({
  etiqueta,
  valor,
  pista,
  destacado,
  exito,
}: {
  etiqueta: string;
  valor: string;
  pista?: string;
  destacado?: boolean;
  exito?: boolean;
}) {
  const color = exito ? 'var(--ilu-success)' : destacado ? 'var(--ilu-primary)' : undefined;
  return (
    <div className={styles.card}>
      <div className={styles.metricLabel}>{etiqueta}</div>
      <div className={styles.metricValue} style={{ color }}>
        {valor}
      </div>
      {pista && <div className={styles.metricHint}>{pista}</div>}
    </div>
  );
}

function Distribucion({ titulo, datos }: { titulo: string; datos: Readonly<Record<string, number>> }) {
  const { t } = useI18n();
  const entradas = Object.entries(datos).sort(([, a], [, b]) => b - a);
  const maximo = Math.max(1, ...entradas.map(([, n]) => n));

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>{titulo}</h3>
      {entradas.length === 0 ? (
        <p className={styles.metricHint}>{t.area.inversionistas.sinDatos}</p>
      ) : (
        <ul className={styles.barras}>
          {entradas.map(([clave, total]) => (
            <li key={clave}>
              {/* El número va escrito además de la barra: el ancho solo no es
                  un dato, y una barra sin cifra no se puede leer en voz alta. */}
              <div className={styles.barraEtiqueta}>
                <span>{clave}</span>
                <strong>{total}</strong>
              </div>
              <div className={styles.barra}>
                <div
                  className={styles.barraRelleno}
                  style={{ width: `${(total / maximo) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * '2026-08' → 'ago 2026' / 'Aug 2026'. Un mes ISO en un eje se lee peor que su
 * nombre.
 *
 * Los nombres salen de `Intl` y no de una tabla propia: el navegador ya trae
 * los doce meses abreviados en cada idioma, y una tabla nuestra habría que
 * traducirla a mano cada vez que se agregue uno.
 */
function nombreDeMes(clave: string, locale: string): string {
  const [anio, mes] = clave.split('-');
  const indice = Number(mes) - 1;
  if (!Number.isInteger(indice) || indice < 0 || indice > 11) return clave;
  // Día 1 y mediodía UTC: evita que el huso horario mueva la fecha de mes.
  const fecha = new Date(Date.UTC(Number(anio), indice, 1, 12));
  const nombre = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(fecha);
  return `${nombre} ${anio}`;
}

function Serie({ titulo, datos }: { titulo: string; datos: readonly PuntoDeSerie[] }) {
  const { t, locale } = useI18n();
  const maximo = Math.max(1, ...datos.map((d) => d.total));

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>{titulo}</h3>
      {datos.length === 0 ? (
        <p className={styles.metricHint}>{t.area.inversionistas.sinDatos}</p>
      ) : (
        <ul className={styles.barras}>
          {datos.map(({ mes, total }) => (
            <li key={mes}>
              <div className={styles.barraEtiqueta}>
                <span>{nombreDeMes(mes, locale)}</span>
                <strong>{total}</strong>
              </div>
              <div className={styles.barra}>
                <div
                  className={styles.barraRelleno}
                  style={{ width: `${(total / maximo) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
